import { message } from "antd";
import { 
    fetchProduct, 
    fetchProductsByIngredient, 
    fetchProductLists, 
    createProductList, 
    addProductsToList, 
    autoCompleteIngredientName, 
    fetchProductCategories
} from "./service";
import { validateCredits } from "@/utils/credits";

const Model = {
    namespace: 'packaging_search',
    state: {
        searchValue: '',
        filters: {},
        productsData: {
            pages: {},
            total: 0,
            ingredientId: null
        },
        currentPage: 1,
        pageSize: 10,
        productLists: [],
        selectedRows: [], // Will store product IDs instead of row indices
        ingredientSuggestions: [],
        packagingSuggestions: [],
        filterDrawerVisible: false,
        listModalVisible: false,
        productCategories: []
    },
    effects: {
        *ingredientAutoComplete({ payload, callback }: any, { call, put }: any) {
            try {
                yield put({ type: 'setIngredientSuggestions', payload: [] });
                const response: Promise<any> = yield call(autoCompleteIngredientName, payload);
                yield put({ type: 'setIngredientSuggestions', payload: response });
            } catch (error) {
                console.error('Failed to fetch suggestions:', error);
            } finally {
                if (callback) callback();
            }
        },
        *clearFilter({ payload }: any, { put, select }: any) {
            const currentState: Promise<any> = yield select((state: any) => state.packaging_search);
            const { filters } = currentState as any;
            
            // Create a new filters object without the specified filter keys
            const updatedFilters = { ...filters };
            
            // Handle payload as either a single key or an array of keys to clear
            const keysToRemove = Array.isArray(payload) ? payload : [payload];
            keysToRemove.forEach(key => {
                delete updatedFilters[key];
            });
            
            // Update the filters state
            yield put({
                type: 'updateState',
                payload: {
                    filters: updatedFilters
                }
            });
            
            // Trigger a search with the updated filters
            yield put({
                type: 'fetchProductsByIngredient',
                payload: {
                    current: 1 // Reset to first page when filters change
                }
            });
        },
        *fetchProductsByIngredient({ payload }: any, { call, put, select }: any) {
            const searchState: Promise<any> = yield select((state: any) => state.packaging_search)
            const { searchValue, filters, pageSize } = searchState as any

            const current = payload?.current || 1
            const _pageSize = payload?.pageSize || pageSize

            const areFiltersEmpty = Object.keys(filters).length === 0
            
            if (areFiltersEmpty) {
                // message.error('Please provide filters before searching.');
                return;
            }

            // Clear cached pages data when initiating a new search (not pagination)
            if (!payload?.isPagination) {
                yield put({
                    type: 'clearProductsData'
                });
            }

            const skip = (current - 1) * _pageSize;
            const take = _pageSize;

            const response: Promise<any> = yield call(fetchProductsByIngredient, {
                take,
                skip,
                filters
            });

            yield put({
                type: `resetPackagingSuggestion`
            })

            yield put({
                type: 'setProductsForPage',
                payload: {
                    page: current,
                    data: (response as any).data,
                    total: (response as any).total,
                    ingredientId: (response as any).ingredient_id
                }
            })

            // Update current page if needed
            yield put({
                type: 'updateState',
                payload: {
                    currentPage: current
                }
            })
        },
        *fetchProduct({ payload, callback }: any, { call, put, select }: any) {
            let searchState: Promise<any> = yield select((state: any) => state.packaging_search)
            const { productsData } = searchState as any;

            const isViewed = Object.values(productsData.pages).some((pageData: any) => {
                return pageData.data.some((product: any) => 
                    product.id === payload.id && product.viewed
                );
            });

            // If already viewed, do nothing
            if (isViewed) {
                if (callback) callback();
                return;
            }

            const { product_credits_remaining } = yield select((state: any) => state.subscription)
            
            if (!validateCredits(product_credits_remaining, 'product')) {
                callback(false);
                return;
            }

            const product: Promise<any> = yield call(fetchProduct, payload)

            // Refresh state to get latest data
            searchState = yield select((state: any) => state.packaging_search)
            const { currentPage }: any = searchState as any
            const pageData = productsData.pages[currentPage]?.data || []

            const updatedPageData = pageData.map((row: any) => {
                if (row.id === (product as any).id) {
                    return {
                        ...product,
                        countries_of_sale: (product as any).countries_of_sale.map((c: any) => c.name).join(", "),
                        marketed_by: (product as any).marketer?.name,
                        manufactured_by: (product as any).manufacturer?.name,
                        viewed: true
                    }
                }
                return row
            })

            yield put({
                type: 'setProductsForPage',
                payload: {
                    page: currentPage,
                    data: updatedPageData,
                    total: productsData.total,
                    ingredientId: productsData.ingredientId
                }
            })

            // deduct credits
            yield put({
                type: 'subscription/deductCredits',
                payload: {
                    product_credits_remaining: product_credits_remaining - 1
                }
            })

            if (callback) callback()
        },
        *fetchProductLists(_: any, { call, put }: any) {
            const response: Promise<any> = yield call(fetchProductLists)
            yield put({
                type: 'setProductLists',
                payload: response
            })
        },
        *createProductList({ payload, callback }: any, { call, put, select }: any) {
            try {
                const response: Promise<any> = yield call(createProductList, payload)
                const existingProductLists: Promise<any> = yield select((state: any) => state.packaging_search.productLists)

                let newProductLists = [...(existingProductLists as any), {
                    id: (response as any).listId,
                    name: payload.name,
                    created_at: new Date().toISOString(),
                }]

                newProductLists = newProductLists.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

                yield put({
                    type: 'setProductLists',
                    payload: newProductLists
                })
                if (callback) callback()

            } catch (error: any) {
                if (callback) callback()
                message.error(error.response.data.message)
            }

        },
        *addProductsToList({ payload, callback }: any, { call, select, put }: any) {
            const { product_credits_remaining } = yield select((state: any) => state.subscription)
            
            if (!validateCredits(product_credits_remaining, 'product')) {
                callback(false);
                return;
            }

            const { listId } = payload
            const { selectedRows } = yield select((state: any) => state.packaging_search)

            const response: Promise<any> = yield call(addProductsToList, {
                listId,
                productIds: selectedRows,
                ingredientId: payload.ingredientId,
                packagingType: payload.packagingType,
                countryOfOrigin: payload.countryOfOrigin,
                countriesOfSale: payload.countriesOfSale,
                category: payload.category
            })

            // Update productsData in the state with unobfuscated product data from response
            if ((response as any)?.success && (response as any)?.products) {
                let searchState: Promise<any> = yield select((state: any) => state.packaging_search);
                const { productsData } = searchState as any;

                // Update product data in all pages where they exist
                const updatedPages = Object.entries(productsData.pages).reduce((acc: any, [pageNum, pageData]: any) => {
                    const updatedData = pageData.data.map((product: any) => {
                        const updatedProduct = (response as any).products.find((p: any) => p.id === product.id);
                        if (updatedProduct) {
                            return {
                                ...updatedProduct,
                                countries_of_sale: updatedProduct.countries_of_sale.map((c: any) => c.name).join(", "),
                                marketed_by: updatedProduct.marketer.name,
                                viewed: true
                            };
                        }
                        return product;
                    });

                    acc[pageNum] = {
                        ...pageData,
                        data: updatedData
                    };
                    return acc;
                }, {});

                yield put({
                    type: 'updateState',
                    payload: {
                        productsData: {
                            ...productsData,
                            pages: updatedPages
                        }
                    }
                });
            }

            if (callback) {
                callback(response)
            }

            if((response as any)) {
                yield put({ 
                    type: 'subscription/deductCredits',
                    payload: {
                        product_credits_remaining: product_credits_remaining - (response as any).credits_to_deduct
                    }
                })
            }
        },
        *fetchProductCategories(_: any, { call, put }: any) {
            const response: Promise<any> = yield call(fetchProductCategories)

            yield put({
                type: 'setProductCategories',
                payload: (response as any).map((category: any) => category.category)
            })
        }
    },
    reducers: {
        updateState(state: any, { payload }: any) {
            return {
                ...state,
                ...payload
            }
        },
        setProductsForPage(state: any, { payload }: any) {
            // payload: { page, data, total }
            const { page, data, total, ingredientId } = payload

            return {
                ...state,
                productsData: {
                    ...state.productsData,
                    pages: {
                        ...state.productsData.pages,
                        [page]: {
                            data,
                            total
                        }
                    },
                    total,
                    ingredientId
                }
            }
        },
        setProductLists(state: any, { payload }: any) {
            return {
                ...state,
                productLists: payload,
            };
        },
        addToSelectedRows(state: any, { payload }: any) {
            // payload is productId
            if (!state.selectedRows.includes(payload)) {
                return {
                    ...state,
                    selectedRows: [...state.selectedRows, payload]
                }
            }
            return state
        },
        removeFromSelectedRows(state: any, { payload }: any) {
            return {
                ...state,
                selectedRows: state.selectedRows.filter((id: any) => id !== payload)
            }
        },
        addAllProductsToSelectedRows(state: any) {
            const { currentPage, productsData } = state
            const currentPageData = productsData.pages[currentPage]?.data || []
            const allIds = currentPageData.map((item: any) => item.id)
            const newSelectedRows = Array.from(new Set([...state.selectedRows, ...allIds]))
            return {
                ...state,
                selectedRows: newSelectedRows
            }
        },
        clearSelectedRows(state: any) {
            return {
                ...state,
                selectedRows: []
            }
        },
        setIngredientSuggestions(state: any, { payload }: any) {
            return {
                ...state,
                ingredientSuggestions: payload
            }
        },
        setPackagingSuggestions(state: any, { payload }: any) {
            return {
                ...state,
                packagingSuggestions: payload
            }
        },
        resetIngredientSuggestion(state: any) {
            return {
                ...state,
                ingredientSuggestions: []
            }
        },
        resetPackagingSuggestion(state: any) {
            return {
                ...state,
                packagingSuggestions: []
            }
        },
        updateSearchValue(state: any, { payload }: any) {
            return {
                ...state,
                searchValue: payload
            };
        },
        updateFilters(state: any, { payload }: any) {
            const cleanedFilters = Object.entries(payload).reduce((acc: any, [key, value]) => {
                if (value) acc[key] = value; 
                return acc;
            }, {});
            
            return {
                ...state,
                filters: cleanedFilters
            }
        },
        resetFilters(state: any) {
            return {
                ...state,
                filters: {},
                searchValue: '',
                productsData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1
            }
        },
        toggleFilterDrawer(state: any) {
            return {
                ...state,
                filterDrawerVisible: !state.filterDrawerVisible
            }
        },
        toggleListModal(state: any) {
            return {
                ...state,
                listModalVisible: !state.listModalVisible
            }
        },
        setProductCategories(state: any, { payload }: any) {
            return {
                ...state,
                productCategories: payload
            }
        },
        resetAll(state: any) {
            return {
                ...state,
                searchValue: '',
                filters: {},
                productsData: {
                    pages: {},
                    total: 0
                },
                selectedRows: [],
                ingredientSuggestions: [],
                currentPage: 1
            }
        },
        clearProductsData(state: any) {
            return {
                ...state,
                productsData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1
            }
        }
    }
}

export default Model;